import {
  DAY_MINUTES,
  type EventRepeat,
  type EventSettings,
  type RuleSettings,
  type SetupOrigin,
} from '@mogzo/shared';

/** The Schedule page's data (M37), as the API sends it. */

export interface Run {
  start: string;
  end: string;
}

export interface ScheduleEvent extends EventSettings {
  id: string;
  status: 'on' | 'scheduled' | 'ended' | 'off';
  run: (Run & { test: boolean }) | null;
  last: Run | null;
  next: Run | null;
  origin: SetupOrigin;
}

export interface ScheduleRule extends RuleSettings {
  id: number;
  last: { at: string; status: 'done' | 'failed'; message: string } | null;
  next: string | null;
  origin: SetupOrigin;
}

export interface ScheduleRun {
  kind: 'on' | 'off' | 'rule';
  sourceId: string;
  at: string;
  status: 'done' | 'failed';
  message: string;
}

export interface Schedule {
  events: ScheduleEvent[];
  rules: ScheduleRule[];
  runs: ScheduleRun[];
  boards: {
    id: string;
    name: string;
    reset: 'none' | 'daily' | 'weekly' | 'monthly';
    sort: 'asc' | 'desc';
  }[];
  achievements: { id: string; name: string }[];
  config: Record<string, unknown>;
  limits: { events: number; rules: number };
  /** How many groups are in Trash, test and live. */
  trash: number;
  now: string;
}

/** What Copy to live shows for one of test's events or rules. */
export interface CopyItem<T> {
  test: T;
  live: T | null;
  state: 'new' | 'changed' | 'same';
  /** What live doesn't have yet, like `achievement weekend-star`. */
  missing: string[];
}

export interface CopyPreview {
  events: CopyItem<ScheduleEvent>[];
  rules: CopyItem<ScheduleRule>[];
  onlyLive: { events: ScheduleEvent[]; rules: ScheduleRule[] };
}

/** One thing in Trash: an event with the rules that went with it, or a rule. */
export interface TrashGroup {
  group: string;
  env: 'test' | 'prod';
  events: ScheduleEvent[];
  rules: ScheduleRule[];
  scores: number;
  deletedAt: string;
  deletedFrom: 'dashboard' | 'cli' | 'copy';
  deletedOn: string | null;
  goneAt: string;
}

/** "2 events and 1 rule": a count of each, for messages. */
export function countOf(events: number, rules: number): string {
  const parts = [
    events && `${events} ${events === 1 ? 'event' : 'events'}`,
    rules && `${rules} ${rules === 1 ? 'rule' : 'rules'}`,
  ].filter(Boolean);
  return parts.join(' and ') || 'nothing';
}

export const WEEKDAYS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];
const SHORT_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const utc = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
  timeZone: 'UTC',
});
const utcDay = new Intl.DateTimeFormat('en-US', {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
  timeZone: 'UTC',
});

/** `Nov 6, 18:00` in UTC. */
export const at = (iso: string) => utc.format(new Date(iso));
/** `Fri, Nov 6, 18:00` in UTC. */
export const atDay = (iso: string) => utcDay.format(new Date(iso));

/** `18:00` from minutes after 00:00 (of the day or week). */
export function clockOf(minute: number): string {
  const inDay = ((minute % DAY_MINUTES) + DAY_MINUTES) % DAY_MINUTES;
  return `${String(Math.floor(inDay / 60)).padStart(2, '0')}:${String(inDay % 60).padStart(2, '0')}`;
}

/** `18:00` as minutes, or null when it isn't a time. */
export function minutesOf(text: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(text.trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  return hours < 24 && minutes < 60 ? hours * 60 + minutes : null;
}

/** "Every week", and "Fri 18:00 – Mon 00:00": how an event's times read in a list. */
export function whenOf(
  event: Pick<EventSettings, 'repeat' | 'startsAt' | 'endsAt' | 'onMinute' | 'offMinute'>,
) {
  if (event.repeat === 'once') {
    return {
      main: 'Once',
      sub: event.startsAt && event.endsAt ? `${at(event.startsAt)} – ${at(event.endsAt)}` : '',
    };
  }
  const weekly = event.repeat === 'weekly';
  const part = (minute: number | null) =>
    minute === null
      ? ''
      : weekly
        ? `${SHORT_DAYS[Math.floor(minute / DAY_MINUTES)]} ${clockOf(minute)}`
        : clockOf(minute);
  return {
    main: weekly ? 'Every week' : 'Every day',
    sub: `${part(event.onMinute)} – ${part(event.offMinute)}`,
  };
}

/** How long each run lasts: "2 days and 6 hours". */
export function lengthOf(repeat: EventRepeat, on: number, off: number): string {
  const cycle = repeat === 'daily' ? DAY_MINUTES : 7 * DAY_MINUTES;
  const minutes = (((off - on) % cycle) + cycle) % cycle;
  const days = Math.floor(minutes / DAY_MINUTES);
  const hours = Math.floor((minutes % DAY_MINUTES) / 60);
  const rest = minutes % 60;
  const parts = [
    days && `${days} ${days === 1 ? 'day' : 'days'}`,
    hours && `${hours} ${hours === 1 ? 'hour' : 'hours'}`,
    rest && `${rest} ${rest === 1 ? 'minute' : 'minutes'}`,
  ].filter(Boolean) as string[];
  return parts.length > 1
    ? `${parts.slice(0, -1).join(', ')} and ${parts.at(-1)}`
    : (parts[0] ?? '');
}

/** A JSON value as it reads in a list: `2`, `"night"`. */
export const shown = (value: unknown) => {
  const text = JSON.stringify(value) ?? 'null';
  return text.length > 40 ? `${text.slice(0, 37)}…` : text;
};

/** "When Weekly best starts fresh", and when that is, for a rule. */
export function ruleWhen(rule: RuleSettings, schedule: Pick<Schedule, 'boards' | 'events'>) {
  if (rule.trigger === 'board_reset') {
    const board = schedule.boards.find((b) => b.id === rule.board);
    const reset = board?.reset;
    return {
      main: `${board?.name ?? rule.board} starts fresh`,
      sub:
        reset === 'daily'
          ? 'Every day at 00:00'
          : reset === 'weekly'
            ? 'Mondays at 00:00'
            : reset === 'monthly'
              ? 'The 1st at 00:00'
              : '',
    };
  }
  if (rule.trigger === 'event_end') {
    const event = schedule.events.find((e) => e.id === rule.event);
    return { main: `${event?.name ?? rule.event} ends`, sub: event ? whenOf(event).main : '' };
  }
  const time = clockOf(rule.atMinute ?? 0);
  return {
    main:
      rule.every === 'day'
        ? `Every day at ${time}`
        : rule.every === 'week'
          ? `Mondays at ${time}`
          : `The 1st at ${time}`,
    sub: 'At set times',
  };
}

/** "Give the top 10 “Weekly champion”", for a rule. */
export function ruleDoes(rule: RuleSettings, achievements: Schedule['achievements']) {
  if (rule.action === 'achievement') {
    const name = achievements.find((a) => a.id === rule.achievement)?.name ?? rule.achievement;
    return {
      lead: `Give the top ${rule.top}`,
      key: null,
      tail: `“${name}”`,
      sub: 'Players tied at the last place get it too',
    };
  }
  const value = rule.value;
  return {
    lead: 'Set',
    key: rule.key,
    tail:
      value?.kind === 'random'
        ? 'to a random number'
        : value?.kind === 'date'
          ? 'to today’s date'
          : `to ${shown(value?.kind === 'fixed' ? value.value : null)}`,
    sub:
      value?.kind === 'random'
        ? `From ${value.min.toLocaleString('en')} to ${value.max.toLocaleString('en')}`
        : value?.kind === 'date'
          ? 'Like "2026-11-04"'
          : 'The same each time',
  };
}
